import React, { useState } from 'react';
import axios from 'axios';

import { SortableTable } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { PencilIcon } from '~/components/icons';
import UpsertDamageAssessmentTypeDialog from '~/components/SystemConfiguration/DamageAssessmentTypes/UpsertDamageAssessmentTypeDialog';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import Switch from '~/components/ui/atoms/Switch';
import type { DATypeConfigInfo } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface DamageAssessmentTypesTableProps {
  damageAssessmentTypes: DATypeConfigInfo[];
  reloadDamageAssessmentTypes: () => void;
  searchTerm: string;
  showOnlyEnabled: boolean;
}

const DamageAssessmentTypesTable: React.FC<DamageAssessmentTypesTableProps> = ({
  damageAssessmentTypes,
  reloadDamageAssessmentTypes,
  searchTerm,
  showOnlyEnabled,
}) => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const [typeToEdit, setTypeToEdit] = useState<DATypeConfigInfo | null>(null);

  const filteredTypes = damageAssessmentTypes
    .filter((row) => row.display_name.includes(searchTerm) || row.key.includes(searchTerm))
    .filter((row) => !showOnlyEnabled || !row.is_disabled);

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/damage_assessment_type_configs`;

  const handleEnable = async (configId: number, newStatus: boolean) => {
    try {
      await axios.patch(`${baseRoute}/${configId}`, { is_disabled: !newStatus });
      await reloadDamageAssessmentTypes();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleUpdate = async (values: Record<string, unknown>) => {
    if (!typeToEdit) return;
    const { key: _key, ...updatePayload } = values;
    await axios.patch(`${baseRoute}/${typeToEdit.id}`, updatePayload);
    await reloadDamageAssessmentTypes();
  };

  const columns = [
    { id: 'display_name', label: 'Name' },
    { id: 'key', label: 'Key' },
    {
      id: 'is_enabled',
      disableSort: true,
      label: 'Enable',
      specialCell: (row: DATypeConfigInfo) => (
        <Switch
          checked={!row.is_disabled}
          onChange={async (_, newStatus) => {
            await handleEnable(row.id, newStatus);
          }}
          disabled={false}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      specialCell: (row: DATypeConfigInfo) => (
        <IconButton onClick={() => setTypeToEdit(row)}>
          <PencilIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <SortableTable columns={columns} rows={filteredTypes} />
      {typeToEdit ? (
        <UpsertDamageAssessmentTypeDialog
          damageAssessmentType={typeToEdit}
          damageAssessmentTypes={damageAssessmentTypes}
          onSubmit={handleUpdate}
          onClose={() => setTypeToEdit(null)}
        />
      ) : null}
    </>
  );
};

export default DamageAssessmentTypesTable;
